import { isPhoneInLandscape, isNotHoverDevice } from '../../../../styles'
import type { Tokens } from '../../../../theming/types'
import type { ModalBaseProps } from '..'

export default (config: {
  props: Pick<ModalBaseProps, 'height' | 'width'>
  tokens: Tokens
}) => {
  const { props, tokens } = config

  function getModalHeight() {
    switch (props.height) {
      case 'window':
        return '100%'
      case 'fixed':
        return 'calc(100vh / 1.3)'
      case 'content':
      default:
        return 'auto'
    }
  }

  function getModalWidth() {
    switch (props.width) {
      case 'xlarge':
        return '900px'
      case 'large':
        return '675px'
      case 'small':
        return '400px'
      case 'medium':
      default:
        return '500px'
    }
  }

  return {
    default: {
      wrap: {
        backgroundColor: tokens.bgColorDefault,
        border: `1px solid ${tokens.modal.borderColor}`,
        borderRadius: tokens.modal.borderRadius,
        color: tokens.fontColorDefault,
        display: 'flex',
        flexDirection: 'column',
        height: getModalHeight(),
        left: '50%',
        marginTop: '36px',
        maxHeight: 'calc(100vh - 36px * 2)',
        position: 'fixed',
        top: 0,
        width: getModalWidth(),

        [isPhoneInLandscape]: {
          marginTop: '6px',
          maxHeight: 'calc(100vh - 6px * 2)',
          width: '100%',
        },

        // Fix for iOS where 100vh actually refers to outerHeight
        // This will account for tablets too as the one above will not
        [isNotHoverDevice]: {
          marginTop: '6px',
          // use % on touch screens (aka mobile devices) to ensure the footer is "sticky" at the bottom of the modal
          maxHeight: 'calc(100% - 6px * 2)',
        },
      },

      close: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
        insetInlineEnd: 0,
        top: 0,
        zIndex: 1,
      },

      modalBase: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      },
    },

    disabled: {
      modalBase: {
        filter: 'grayscale(100%)',

        '&::after': {
          backgroundColor: 'transparent',
          bottom: '-1px',
          content: '""',
          insetInlineStart: '-1px',
          position: 'absolute',
          insetInlineEnd: '-1px',
          top: '-1px',
        },
      },

      close: {
        visibility: 'hidden',
      },
    },

    motionVariants: {
      wrap: {
        // This is necessary because framer-motion sets it to transform:none in preparation for the animation.
        initial: { x: '-50%' },

        enterScale: {
          scale: [1.1, 1] as number[],
          transition: {
            ease: 'default',
            duration: 0.25,
            delay: 0.15,
          },
        },

        enterOpacity: {
          opacity: [0, 1] as number[],
          transition: {
            ease: 'harmonize',
            duration: 0.25,
            delay: 0.15,
          },
        },

        exitScale: {
          scale: [1, 0.9] as number[],
          transition: {
            ease: 'default',
            duration: 0.15,
          },
        },

        exitOpacity: {
          opacity: [1, 0] as number[],
          transition: {
            ease: 'harmonize',
            duration: 0.15,
          },
        },
      },
    },
  } as const
}
